import { Dispatch } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action } from 'redux';

import lang from '../../lib/language';
import { BaseStatementSearchParamType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getStatementsIncome } from '../../lib/store/fundamental';

import { FinancialsTableRow } from '../../components';
import FinancialsDivRow from '../../shared_components/FinancialsDivRow/FinancialsDivRow';

import FinancialStatementLayout from './FinancialStatementLayout';
import useFinancialStatement from './useFinancialStatement';

const IncomeStatement = (props: ReduxProps) => {
  const { getStatements, income } = props;

  const {
    headers,
    isMobile,
    loading,
    period,
    row,
    selectedColumn,
    selectedPeriodIndex,
    selectPeriod,
    setSelectedPeriodIndex,
    timeOptions,
  } = useFinancialStatement({
    items: income,
    isReady: income.length > 0,
    labelPrefix: 'income',
    getStatements,
  });

  return (
    <FinancialStatementLayout
      title={lang.financialsIncomeStatement()}
      preloaderOwner="income-statement"
      loading={loading}
      isMobile={isMobile}
      period={period}
      onPeriodChange={selectPeriod}
      headers={headers}
      timeOptions={timeOptions}
      selectedPeriodIndex={selectedPeriodIndex}
      onPeriodIndexChange={setSelectedPeriodIndex}
      mobileRows={(
        <>
          <FinancialsTableRow
            data={[ row.revenue, row.costOfRevenue ]}
            isClickable
            currentPeriod={selectedColumn}
          />
          <FinancialsTableRow data={[ row.grossProfit ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow
            data={[
              row.operatingExpenses,
              row.researchAndDevelopmentExpenses,
              row.sellingGeneralAndAdministrativeExpenses,
            ]}
            isClickable
            currentPeriod={selectedColumn}
          />
          <FinancialsTableRow
            data={[ row.operatingIncome, row.totalOtherIncomeExpensesNet ]}
            isClickable
            currentPeriod={selectedColumn}
          />
          <FinancialsTableRow
            data={[ row.incomeBeforeTax, row.incomeTaxExpense ]}
            isClickable
            currentPeriod={selectedColumn}
          />
          <FinancialsTableRow data={[ row.netIncome ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow data={[ row.eps ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow data={[ row.epsDiluted ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow data={[ row.weightedAverageShsOut ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow data={[ row.weightedAverageShsOutDil ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow data={[ row.interestIncome ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow data={[ row.interestExpense ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow data={[ row.ebitda ]} currentPeriod={selectedColumn} />
        </>
      )}
      desktopRows={(
        <>
          <FinancialsDivRow data={row.revenue} subData={[ row.costOfRevenue ]} isClickable />
          <FinancialsDivRow data={row.grossProfit} />
          <FinancialsDivRow
            data={row.operatingExpenses}
            subData={[ row.researchAndDevelopmentExpenses, row.sellingGeneralAndAdministrativeExpenses ]}
            isClickable
          />
          <FinancialsDivRow data={row.operatingIncome} subData={[ row.totalOtherIncomeExpensesNet ]} isClickable />
          <FinancialsDivRow data={row.incomeBeforeTax} subData={[ row.incomeTaxExpense ]} isClickable />
          <FinancialsDivRow data={row.netIncome} />
          <FinancialsDivRow data={row.eps} />
          <FinancialsDivRow data={row.epsDiluted} />
          <FinancialsDivRow data={row.weightedAverageShsOut} />
          <FinancialsDivRow data={row.weightedAverageShsOutDil} />
          <FinancialsDivRow data={row.interestIncome} />
          <FinancialsDivRow data={row.interestExpense} />
          <FinancialsDivRow data={row.ebitda} />
        </>
      )}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  income: state.fundamental.statementsData.income,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsIncome(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(IncomeStatement);
